import React from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { ReportFooterTimestamp } from '../../../utils/reportUtils';
import useCurrency from '../../CurrencyProvider';
import { ClinicHeader } from '../ClinicHeader';
import PatientDetailsForReport from '../PatientDetailsForReport';
import { PharmacyInvoiceDetailsResponse } from '../../../pages/Billing/Pharmacy/viewPharmacyInvoice.helper';
import { PharmacyPaymentTransaction } from '../../../pages/Billing/Pharmacy/pharmacyReceiptHelper';
import { formatDateShortMonth } from '../../../utils/dateUtils';

type Props = {
  invoiceData: PharmacyInvoiceDetailsResponse;
  transactions?: PharmacyPaymentTransaction[];
};

const PharmacyInvoiceReport: React.FC<Props> = ({ invoiceData, transactions = [] }) => {
  const { formatCurrency } = useCurrency();

  const { invoice_info, current_prescriptions, previous_prescriptions } = invoiceData.data;

  const {
    bill_number,
    bill_type,
    visit_date,
    total_amount,
    concession_amount,
    discount_amount,
    tax_amount,
    net_amount,
    paid_amount,
    balance_amount,
    concession_reason,
    due_date,
    terms_and_conditions,
    legal_mentions,
    remark,
    comments,
    status,
    patient_id,
  } = invoice_info;

  const allItems = [...current_prescriptions, ...previous_prescriptions];

  const cellStyle = { py: 0.75, px: 1, fontSize: 12, borderBottom: '1px solid #e8e8e8', color: '#444' };
  const headerCellStyle = { ...cellStyle, fontWeight: 600, backgroundColor: '#f7f7f7', color: '#555', borderBottom: '1px solid #ddd' };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: '#fff',
        color: '#333',
        fontFamily: 'Arial, sans-serif',
        fontSize: 12,
        letterSpacing: 0,
      }}
    >
      {/* Clinic Header */}
      <ClinicHeader />

      {/* Patient Details Header */}
      <PatientDetailsForReport patientId={patient_id} />

      {/* Invoice Title */}
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          mt: 2,
          mb: 1.5,
          fontSize: 14,
          color: '#333',
          borderBottom: '1px solid #e0e0e0',
          pb: 0.5,
        }}
      >
        Pharmacy Invoice Details
      </Typography>

      {/* Invoice Details Row */}
      <Box sx={{ mb: 2 }}>
        <Table size="small" sx={{ border: '1px solid #e8e8e8' }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ ...cellStyle, fontWeight: 600, width: '15%' }}>Bill No:</TableCell>
              <TableCell sx={{ ...cellStyle, width: '35%' }}>{bill_number || 'N/A'}</TableCell>
              <TableCell sx={{ ...cellStyle, fontWeight: 600, width: '15%' }}>Status:</TableCell>
              <TableCell sx={{ ...cellStyle, width: '35%', textTransform: 'capitalize' }}>{status || 'N/A'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Visit Date:</TableCell>
              <TableCell sx={cellStyle}>{visit_date ? formatDateShortMonth(visit_date) : 'N/A'}</TableCell>
              <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Due Date:</TableCell>
              <TableCell sx={cellStyle}>{due_date ? formatDateShortMonth(due_date) : 'N/A'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Bill Type:</TableCell>
              <TableCell sx={cellStyle} colSpan={3}>{bill_type === 'PH' ? 'Pharmacy' : bill_type || 'N/A'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {/* Pharmacy Items Table */}
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontSize: 12, color: '#444' }}>
        Pharmacy Items
      </Typography>
      <TableContainer sx={{ mb: 2, border: '1px solid #e8e8e8' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellStyle, width: 30 }}>#</TableCell>
              <TableCell sx={headerCellStyle}>Item Name</TableCell>
              <TableCell sx={headerCellStyle}>Batch No</TableCell>
              <TableCell sx={headerCellStyle}>Expiry Date</TableCell>
              <TableCell sx={headerCellStyle}>Manufacturer</TableCell>
              <TableCell sx={headerCellStyle}>Strength</TableCell>
              <TableCell sx={headerCellStyle} align="center">Qty</TableCell>
              <TableCell sx={headerCellStyle} align="right">MRP</TableCell>
              <TableCell sx={headerCellStyle} align="right">Concession</TableCell>
              <TableCell sx={headerCellStyle} align="right">Tax</TableCell>
              <TableCell sx={headerCellStyle} align="right">Net Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allItems.map((item, index) => (
              <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                <TableCell sx={cellStyle}>{index + 1}</TableCell>
                <TableCell sx={cellStyle}>
                  {item.item_name}
                  {item.item_strength && ` (${item.item_strength})`}
                </TableCell>
                <TableCell sx={cellStyle}>{item.batch_number || '-'}</TableCell>
                <TableCell sx={cellStyle}>
                  {item.expiry_date ? formatDateShortMonth(item.expiry_date) : '-'}
                </TableCell>
                <TableCell sx={cellStyle}>{item.manufacturer || '-'}</TableCell>
                <TableCell sx={cellStyle}>
                  {item.billed_strength || item.prescribed_strength || '-'}
                </TableCell>
                <TableCell sx={cellStyle} align="center">
                  {item.billed_quantity} {item.billed_uom}
                </TableCell>
                <TableCell sx={cellStyle} align="right">{formatCurrency(Number(item.total_mrp_amount) || 0)}</TableCell>
                <TableCell sx={cellStyle} align="right">{formatCurrency(Number(item.concession_amount) || 0)}</TableCell>
                <TableCell sx={cellStyle} align="right">
                  {formatCurrency(Number(item.tax_amount) || 0)}
                  {item.sgst_percentage > 0 && (
                    <Typography component="span" sx={{ fontSize: 10, display: 'block', color: '#888' }}>
                      (SGST: {item.sgst_percentage}% + CGST: {item.cgst_percentage}%)
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ ...cellStyle, fontWeight: 600 }} align="right">{formatCurrency(Number(item.net_amount) || 0)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Payment Transactions */}
      {transactions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontSize: 12, color: '#444' }}>
            Payment Details
          </Typography>
          <TableContainer sx={{ border: '1px solid #e8e8e8' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: 40 }}>#</TableCell>
                  <TableCell sx={headerCellStyle}>Payment Mode</TableCell>
                  <TableCell sx={headerCellStyle}>Date</TableCell>
                  <TableCell sx={headerCellStyle}>Reference No.</TableCell>
                  <TableCell sx={headerCellStyle}>Status</TableCell>
                  <TableCell sx={headerCellStyle} align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction, index) => (
                  <TableRow key={transaction.payment_detail_id}>
                    <TableCell sx={cellStyle}>{index + 1}</TableCell>
                    <TableCell sx={cellStyle}>{transaction.payment_mode_name}</TableCell>
                    <TableCell sx={cellStyle}>{formatDateShortMonth(transaction.date)}</TableCell>
                    <TableCell sx={cellStyle}>{transaction.reference_number || '-'}</TableCell>
                    <TableCell
                      sx={{
                        ...cellStyle,
                        color: transaction.is_refunded ? '#d32f2f' : '#2e7d32',
                        fontWeight: 500,
                      }}
                    >
                      {transaction.is_refunded ? 'Refunded' : 'Paid'}
                    </TableCell>
                    <TableCell sx={cellStyle} align="right">
                      {formatCurrency(Number(transaction.paid_amount) || 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}


      {/* Summary Table */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Table size="small" sx={{ width: 280, border: '1px solid #e8e8e8' }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ ...cellStyle, fontWeight: 500 }}>Total Amount:</TableCell>
              <TableCell sx={cellStyle} align="right">{formatCurrency(Number(total_amount) || 0)}</TableCell>
            </TableRow>
            {Number(concession_amount) > 0 && (
              <TableRow>
                <TableCell sx={{ ...cellStyle, fontWeight: 500 }}>Concession:</TableCell>
                <TableCell sx={cellStyle} align="right">(-) {formatCurrency(Number(concession_amount) || 0)}</TableCell>
              </TableRow>
            )}
            {Number(discount_amount) > 0 && (
              <TableRow>
                <TableCell sx={{ ...cellStyle, fontWeight: 500 }}>Discount:</TableCell>
                <TableCell sx={cellStyle} align="right">(-) {formatCurrency(Number(discount_amount) || 0)}</TableCell>
              </TableRow>
            )}
            {Number(tax_amount) > 0 && (
              <TableRow>
                <TableCell sx={{ ...cellStyle, fontWeight: 500 }}>Tax:</TableCell>
                <TableCell sx={cellStyle} align="right">(+) {formatCurrency(Number(tax_amount) || 0)}</TableCell>
              </TableRow>
            )}
            <TableRow sx={{ backgroundColor: '#f7f7f7', borderTop: '1px solid #ddd' }}>
              <TableCell sx={{ ...cellStyle, fontWeight: 700, fontSize: 12.5 }}>Net Amount:</TableCell>
              <TableCell sx={{ ...cellStyle, fontWeight: 700, fontSize: 12.5 }} align="right">{formatCurrency(Number(net_amount) || 0)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...cellStyle, fontWeight: 500 }}>Paid Amount:</TableCell>
              <TableCell sx={cellStyle} align="right">{formatCurrency(Number(paid_amount) || 0)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...cellStyle, fontWeight: 500 }}>Balance:</TableCell>
              <TableCell
                sx={{
                  ...cellStyle,
                  fontWeight: Number(balance_amount) > 0 ? 600 : 400,
                  color: Number(balance_amount) > 0 ? '#c62828' : 'inherit',
                }}
                align="right"
              >
                {formatCurrency(Number(balance_amount) || 0)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      {/* Additional Information */}
      {concession_reason && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
            Concession Reason:{' '}
          </Typography>
          <Typography variant="body2" component="span">
            {concession_reason}
          </Typography>
        </Box>
      )}

      {terms_and_conditions && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Terms and Conditions:</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{terms_and_conditions}</Typography>
        </Box>
      )}

      {legal_mentions && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Legal Mentions:</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{legal_mentions}</Typography>
        </Box>
      )}

      {remark && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
            Remark:{' '}
          </Typography>
          <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap' }}>
            {remark}
          </Typography>
        </Box>
      )}

      {comments && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
            Comments:{' '}
          </Typography>
          <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap' }}>
            {comments}
          </Typography>
        </Box>
      )}

      <Box sx={{ borderTop: '1px solid #e8e8e8', mt: 2, pt: 1 }}>
        <ReportFooterTimestamp /> 
      </Box>
    </Box>
  );
};

export default PharmacyInvoiceReport;
